import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, db } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('Error getting session:', error);
          // Clear potentially corrupted session data
          try {
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
          } catch (clearError) {
            console.error('Error clearing session:', clearError);
          }
          setUser(null);
          setUserProfile(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          setUser(session.user);
          await loadUserProfile(session.user.id);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setUserProfile(null);
          setIsAuthenticated(false);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setUser(null);
          setUserProfile(null);
          setIsAuthenticated(false);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      console.log('Auth state changed:', event, session?.user?.id);
      
      if (session?.user) {
        setUser(session.user);
        await loadUserProfile(session.user.id);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const getInitialSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        // Clear potentially corrupted session data
        try {
          await supabase.auth.signOut();
          localStorage.removeItem('supabase.auth.token');
        } catch (clearError) {
          console.error('Error clearing session:', clearError);
        }
        return;
      }
      
      if (session?.user) {
        setUser(session.user);
        await loadUserProfile(session.user.id);
        setIsAuthenticated(true);
      } else {
        // No session, ensure clean state
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error getting initial session:', error);
      // Clear potentially corrupted session data
      try {
        await supabase.auth.signOut();
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
      } catch (clearError) {
        console.error('Error clearing session:', clearError);
      }
      
      // Ensure clean state
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async (userId) => {
    try {
      // Set a timeout for profile loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile loading timeout')), 10000);
      });
      
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      const { data, error } = await Promise.race([profilePromise, timeoutPromise]);
      
      if (error) {
        // If profile doesn't exist, create one with default student role
        if (error.code === 'PGRST116') {
          console.log('Profile not found, creating new profile...');
          const { data: insertedProfile, error: insertError } = await supabase
            .from('profiles')
            .insert([{
              user_id: userId,
              email: user?.email || '',
              full_name: user?.user_metadata?.full_name || '',
              role: 'student',
              is_active: true
            }])
            .select()
            .single();
          
          if (!insertError) {
            setUserProfile(insertedProfile);
          } else {
            console.error('Error creating profile:', insertError);
            // Set a basic profile to avoid infinite loading
            setUserProfile({
              user_id: userId,
              email: user?.email || '',
              full_name: user?.user_metadata?.full_name || '',
              role: 'student',
              is_active: true
            });
          }
        } else {
          console.error('Error loading profile:', error);
          // Set a basic profile to avoid infinite loading
          setUserProfile({
            user_id: userId,
            email: user?.email || '',
            full_name: user?.user_metadata?.full_name || '',
            role: 'student',
            is_active: true
          });
        }
      } else {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Set a basic profile to avoid infinite loading
      setUserProfile({
        user_id: userId,
        email: user?.email || '',
        full_name: user?.user_metadata?.full_name || '',
        role: 'student',
        is_active: true
      });
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      // First check if user already exists in profiles (they might be a client)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();
      
      if (existingProfile) {
        return { 
          data: null, 
          error: { 
            message: `An account with this email already exists. If you're a support client, please use the login credentials sent to your email. If you're a student, please use a different email address or contact support.`
          }
        };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata.full_name || '',
            ...metadata
          }
        }
      });

      if (!error && data.user) {
        // Create profile with student role by default
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .insert([{
              user_id: data.user.id,
              email: email,
              full_name: metadata.full_name || '',
              role: 'student',
              is_active: true
            }])
            .select()
            .single();
          
          if (profileError) {
            console.error('Error creating profile:', profileError);
            // If profile creation fails, still return success but log the error
            // The profile will be created automatically on first login via loadUserProfile
          }
        } catch (profileError) {
          console.error('Error creating profile:', profileError);
          // Continue with signup success even if profile creation fails
        }
      }

      return { data, error };
    } catch (error) {
      console.error('Signup error:', error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      // Always clear local state regardless of error
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      
      // Clear any cached data
      try {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
      } catch (clearError) {
        console.error('Error clearing cached data:', clearError);
      }
      
      return { error };
    } catch (error) {
      // Even if signOut fails, clear local state
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      
      try {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
      } catch (clearError) {
        console.error('Error clearing cached data:', clearError);
      }
      
      return { error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) return { data: null, error: new Error('No user logged in') };
      
      const { data, error } = await db.updateProfile(user.id, updates);
      if (!error) {
        setUserProfile(data);
      }
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Role-based helper functions
  const hasRole = (role) => {
    return userProfile?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(userProfile?.role);
  };

  const isAdmin = () => hasRole('admin');
  const isClient = () => hasRole('client');
  const isStudent = () => hasRole('student');

  const canAccessAdmin = () => isAdmin();
  const canAccessSupport = () => hasAnyRole(['admin', 'client']);
  const canAccessAcademy = () => hasAnyRole(['admin', 'student']);

  const value = {
    user,
    userProfile,
    loading,
    isAuthenticated,
    signUp,
    signIn,
    signOut,
    updateProfile,
    // Role-based helpers
    hasRole,
    hasAnyRole,
    isAdmin,
    isClient,
    isStudent,
    canAccessAdmin,
    canAccessSupport,
    canAccessAcademy,
    // Reload profile function
    reloadProfile: () => loadUserProfile(user?.id)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};